interface DebouncedFunction<T extends (...args: unknown[]) => unknown> {
  (...args: Parameters<T>): void;
  cancel: () => void;
}

/**
 * Creates a debounced function that delays invoking `fn` until after `timeout` milliseconds
 * have elapsed since the last time the debounced function was invoked.
 *
 * @param fn The function to debounce
 * @param timeout The number of milliseconds to delay
 * @returns A debounced version of the function with a cancel method
 *
 * @example
 * const debouncedFn = debounce((value: string) => {
 *   console.log(value);
 * }, 500);
 *
 * // Usage
 * debouncedFn('test');  // Will only log after 500ms of no calls
 * debouncedFn.cancel(); // Cancels pending execution
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  timeout = 500
): DebouncedFunction<T> {
  let timeoutId: NodeJS.Timeout | undefined;
  let latestArgs: Parameters<T> | undefined;

  // The actual debounced function
  function debounced(...args: Parameters<T>): void {
    latestArgs = args;

    // Clear any existing timeout
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    // Set new timeout
    timeoutId = setTimeout(() => {
      if (latestArgs) {
        fn(...latestArgs);
        // Clear the args and timeout ID after execution
        latestArgs = undefined;
        timeoutId = undefined;
      }
    }, timeout);
  }

  // Add cancel method
  debounced.cancel = function cancel(): void {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = undefined;
    }
    latestArgs = undefined;
  };

  return debounced;
}
